import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as agent from "../services/agentAPI";

//* Get Agent Details
export const useGetAgentByUserId = (params) => {
  return useQuery({
    queryKey: ["Agent-Details", params],
    queryFn: () => agent.getAgentByUserId({ userId: params }),

    enabled: !!params,
    retry: false,
  });
};

//? Home Services List
export const useHomeServicesList = () => {
  return useMutation({
    mutationFn: (data) => agent.getAgentServicesWithFilter(data),
    retry: false,
  });
};

//? Alert List
export const useAgentAlertWithFilter = () => {
  return useMutation({
    mutationFn: (data) => agent.getAgentAlertWithFilter(data),
    retry: false,
  });
};

//* Get Service & Product Details
export const useGetServiceProductDetails = (params) => {
  return useQuery({
    queryKey: ["Service-Details", params],
    queryFn: () => agent.getServiceProductDetails(params),

    enabled: !!params?.serviceId,
    retry: false,
    refetchInterval: 5000,
  });
};

//* Get Profile DP
export const useGetProfileDPforAgent = (params) => {
  return useQuery({
    queryKey: ["Service-Details", params],
    queryFn: () => agent.getProfileDP(params),

    enabled: !!params?.userId,
    retry: false,
  });
};

//* Get Member Details by UserId
export const useGetMemberDetailsForAgent = (params) => {
  return useQuery({
    queryKey: ["Member-Details", params],
    queryFn: () => agent.getMemberDetails({ userId: params }),

    enabled: !!params,
    retry: false,
  });
};

//* Get Service History Details
export const useGetAgentServiceHistory = (params) => {
  return useQuery({
    queryKey: ["Service-Details-History", params],
    queryFn: () => agent.getServiceStatusHistory({ serviceId: params }),

    enabled: !!params,
    retry: false,
  });
};

//? Update Start Service Status
export const useStartServiceByAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.startServiceByAgent(data),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries("Service-Details"),
  });
};

//? Update Service Status
export const useUpdateServiceStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.updateAgentServiceStatus(data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries("Service-Details");
      queryClient.invalidateQueries("Service-Details-History");
    },
  });
};

//? Fitcoin Service Status
export const useFitcoinServiceStatus = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mutationData) =>
      agent.createRewardsForAllService(mutationData),
    retry: false,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries("Service-Details");
      queryClient.invalidateQueries("Service-Details-History");
      if (typeof options.onSuccess === "function") {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (typeof options.onError === "function") {
        options.onError(error, variables, context);
      }
    },
  });
};

//? Reassign to Self
export const useReassignToSelf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.updateReassignToSelf(data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries("Service-Details");
      queryClient.invalidateQueries("Service-Details-History");
    },
  });
};

//? Update Product Status
export const useUpdateProductStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.updateProductStatus(data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries("Service-Details");
      queryClient.invalidateQueries("Service-Details-History");
    },
  });
};

//* Get Service Chat History Details
export const useGetServiceChatHistory = (params) => {
  return useQuery({
    queryKey: ["Service-Chat", params],
    queryFn: () => agent.getServiceChatHistory({ serviceNumber: params }),
    enabled: !!params,
    retry: false,
    refetchInterval: 2000,
  });
};

//* Get Service Chat History without polling
export const useGetServiceChatHistoryNoPolling = (params) => {
  return useQuery({
    queryKey: ["Service-Chat-No", params],
    queryFn: () => agent.getServiceChatHistory({ serviceNumber: params }),

    enabled: !!params,
    retry: false,
    refetchInterval: 10000,
  });
};

//? Send Chat Reply
export const useSendChatReplyAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.sendChatReplyAgent(data),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries("Service-Chat"),
  });
};

//? Generate Razorpay Link
export const useGenrateRazorpayLink = () => {
  return useMutation({
    mutationFn: (data) => agent.getRazorpayLink(data),
    retry: false,
  });
};

//* Get Product History
export const useGetProductHistory = (params) => {
  return useQuery({
    queryKey: ["Product-History", params],
    queryFn: () => agent.getSingleProductHistory(params),
    enabled: !!params,
    retry: false,
  });
};

//* Get New Message List
export const useGetNewMessageList = (params) => {
  return useQuery({
    queryKey: ["New-Message-List", params],
    queryFn: () => agent.getNewMessageList(params),

    retry: false,
    refetchInterval: 150000,
  });
};

//* Get Profile Values
export const useGetMemberProfileForAgent = (params) => {
  return useQuery({
    queryKey: ["Profile-Values-Agent", params],
    queryFn: () => agent.getProfileValuesAgent(params),

    enabled: !!params?.memberId,
    retry: false,
  });
};

//? Write Prescription - Med Agent
export const useAgentPrescription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.agentPrescription(data),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries("Service-Details"),
  });
};

//* Get Generic Medicine List
export const useGenericAgentMedicine = (params) => {
  return useQuery({
    queryKey: ["Generic-Medicine-Agent", params],
    queryFn: () => agent.getGenericMedicine({ genericMedicineName: params }),

    enabled: !!params,
    retry: false,
  });
};

//? Case Notes for Product
export const useProductCaseNotes = () => {
  return useMutation({
    mutationFn: (data) => agent.getProductCaseNotes(data),
    retry: false,
  });
};

//* Get Docs By ID
export const useGetDocsById = () => {
  return useMutation({
    mutationFn: (data) => agent.getDocsByIdAgent(data),
    retry: false,
  });
};

//? Upload Lab Report
export const useUploadLabReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.uploadLabReport(data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries("Service-Details");
      queryClient.invalidateQueries("Service-Details-History");
    },
  });
};

//? Delete SO Record
export const useDeleteSORecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => agent.updateMemberScanReportStatus(data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries("Service-Details");
      queryClient.invalidateQueries("Service-Details-History");
    },
  });
};

//* Get Menu Status
export const useGetDashboardStatusAgent = () => {
  return useQuery({
    queryKey: ["Dashboard-MenuStatus"],
    queryFn: () => agent.getDashboardStatusAgent(),
    retry: false,
    refetchInterval: 8000,
    refetchIntervalInBackground: true,
  });
};
